import React, { useState, useRef, useEffect } from 'react';
import { href, Link } from 'react-router-dom';
import { 
  ChevronDown, 
  Home, 
  Globe, 
  Briefcase, 
  Calendar, 
  Users,
  Building,
  Film,
  Zap,
  GraduationCap,
  Heart,
  Leaf,
  Box,
  Truck,
} from 'lucide-react';
import logo1 from '../../../assets/Image (3).jpg';

// Import product logos
import falconLogo from '../../../assets/falcon.png';
import cheetahLogo from '../../../assets/cheetah.png';
import owlLogo from '../../../assets/owl.png';
import appConfig from '../../../config/appConfig';
import header from '../../../assets/icon/header.svg';

export default function Header(){
const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRefs = useRef([]);

  // Define dropdown menu items for each header link with product logos
  const navLinks = [
    {
      title: 'Products',
      href: '/',
      icon: <Box size={18} className="mr-1" />,
      dropdownItems: [
        { 
          title: 'Falcon Mapping', 
          href: "/falcon",
          logo:<img src={falconLogo} alt="Falcon Logo" className="h-5 w-5 mr-2" /> // Falcon logo (using Bird icon as placeholder)
        },
        { 
          title: 'Code Cheetah', 
          href: appConfig.PRODUCT_CHEETAH_URL,
logo: <img src={cheetahLogo} alt="Falcon Logo" className="h-5 w-5 mr-2" />


        },
        { 
          title: 'Owl Sight', 
          href: appConfig.PRODUCT_OWL_URL,
logo: <img src={owlLogo} alt="Falcon Logo" className="h-5 w-5 mr-2" />


        },
      ]
    },
    {
      title: 'Industry',
      href: '#opportunities',
      icon: <Briefcase size={18} className="mr-1" />,
      dropdownItems: [
        { title: 'Manufacturing', href: '/manufacturing' },
        { title: 'HR & Staffing', href: '/hr' },
        {title: 'Logistics',href: '/logistics'},
      ]
    },
    {
      title: 'About Us',
      href: '#contact',
      icon: <Users size={18} className="mr-1" />,
      dropdownItems: [
        { title: 'About Us', href: '/about' },
      ]
    }
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (activeDropdown !== null && 
          dropdownRefs.current[activeDropdown] && 
          !dropdownRefs.current[activeDropdown].contains(event.target)) {
        setActiveDropdown(null);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeDropdown]);

  const toggleDropdown = (index) => {
    if (activeDropdown === index) {
      setActiveDropdown(null);
    } else {
      setActiveDropdown(index);
    }
  };

  // Function to determine dropdown position class
  const getDropdownPositionClass = (index) => {
    // If it's the last dropdown (Friends), align it to the right
    if (index === navLinks.length - 1) {
      return "right-0"; // Align to right edge of parent
    } else if (index === navLinks.length - 2) {
      return "right-0"; // Also align Account dropdown to right 
    }
    return "left-0"; // Default alignment for other dropdowns
  };

  // Icons for dropdown items
  const getIconForDropdownItem = (title) => {
    switch (title.toLowerCase()) {
      case 'environment':
        return <Globe size={16} className="mr-2" />;
      case 'tourism':
        return <Briefcase size={16} className="mr-2" />;
      case 'entertainment':
        return <Film size={16} className="mr-2" />;
      case 'energy':
        return <Zap size={16} className="mr-2" />;
      case 'education':
        return <GraduationCap size={16} className="mr-2" />;
      case 'events':
        return <Calendar size={16} className="mr-2" />;
      case 'blogs':
        return <Globe size={16} className="mr-2" />;
      case 'strengthen our journey':
        return <Users size={16} className="mr-2" />;
      case 'agri-tech':
        return <Leaf size={16} className="mr-2"/>;
      case 'manufacturing':
        return <Building size={16} className="mr-2" />;
      case 'hr & staffing':
        return <Users size={16} className="mr-2" />;
      case 'about us':
        return <Heart size={16} className="mr-2" />;
      case 'logistics':
        return <Truck size={16} className="mr-2" />;
      default:
        return null;
    }
  };

  return (
    <header className="bg-white shadow-md sticky top-0 z-10">
      <div className="w-full px-4 flex justify-between items-center">
          <Link to="/">
         <img src={logo1} alt="Bluegecko" className="h-24 w-auto" />
         </Link>
        {/* Desktop Navigation with Dropdowns */}
        <nav className="hidden md:flex space-x-8 relative ml-auto">
          {navLinks.map((link, index) => (
            <div 
              key={index} 
              className="relative group" 
              ref={el => dropdownRefs.current[index] = el}
            >
              <button 
                className="text-gray-700 hover:text-blue-600 font-medium flex items-center"
                onClick={() => toggleDropdown(index)}
              >
                {link.icon}
                {link.title}
                <ChevronDown className="ml-1" size={16} />
              </button>
              
              {activeDropdown === index && (
                <div 
                  className={`absolute top-full ${getDropdownPositionClass(index)} mt-2 w-48 bg-white shadow-lg rounded-md py-2 z-20`}
                >
                  {link.dropdownItems.map((item, itemIndex) => (
                    <Link 
                      key={itemIndex} 
                      to={item.href}
                      className="px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 flex items-center"
                    >
                      {/* Show product logo for Products dropdown, or default icon for other dropdowns */}
                      {link.title === 'Products' ? item.logo : getIconForDropdownItem(item.title)}
                      {item.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Mobile menu button */}
        <div className="md:hidden">
          <button 
            onClick={() => setIsMenuOpen(!isMenuOpen)} 
            className="text-gray-700"
          >
            <img src={header} alt="Home Icon"/>
          </button>
        </div>
      </div>

      {/* Mobile Navigation with Accordions */}
      {isMenuOpen && (
        <div className="md:hidden bg-white py-2 px-4 shadow-lg">
          {navLinks.map((link, index) => (
            <div key={index} className="border-b border-gray-100 last:border-b-0">
              <button 
                onClick={() => toggleDropdown(index)}
                className="flex justify-between items-center w-full py-2 text-gray-700 hover:text-blue-600"
              >
                <span className="flex items-center">
                  {link.icon}
                  {link.title}
                </span>
                <ChevronDown 
                  className={`transform transition-transform ${activeDropdown === index ? 'rotate-180' : ''}`} 
                  size={16} 
                />
              </button>
              
              {activeDropdown === index && (
                <div className="pl-4 pb-2">
                  {link.dropdownItems.map((item, itemIndex) => (
                    <Link 
                      key={itemIndex} 
                      to={item.href}
                      className="py-2 text-gray-600 hover:text-blue-600 flex items-center"
                    >
                      {/* Show product logo for Products dropdown, or default icon for other dropdowns */}
                      {link.title === 'Products' ? item.logo : getIconForDropdownItem(item.title)}
                      {item.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </header>
  );
}